import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  Subscriptions as SubscriptionsIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ConfirmDialog from '../common/ConfirmDialog';
import StoreService from '../../services/storeService';
import { useToast } from '../../context/ToastContext';
import { normalizeLanguage } from '../../constants/Languages';
import { isEffectivePro } from '../../utils/storePlan';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const isTrialPlan = (plan) => String(plan || '').trim().toLowerCase() === 'trial';

const isBaseSubscriptionPlan = (plan) => plan === 'BaseSubscription' || plan === 2;

const formatTrialEndDate = (dateUtc) => {
  if (!dateUtc) {
    return '';
  }

  return format(new Date(dateUtc), 'dd/MM/yyyy');
};

const formatSubscriptionPrice = (amount, currency, language) => {
  if (amount == null || !currency) {
    return null;
  }

  const locale = normalizeLanguage(language) === 'pt'
    ? 'pt-PT'
    : normalizeLanguage(language) === 'es'
      ? 'es-ES'
      : 'en-GB';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
};

const sectionContentPadding = { xs: 2.5, sm: 3, md: 4 };

const innerSectionStyles = {
  p: { xs: 2, sm: 2.5 },
  backgroundColor: BRAND.surface,
  borderRadius: 2,
  border: `1px solid ${BRAND.line}`,
};

const StoreSubscriptionPanel = ({
  storeId,
  billingStatus,
  billingStatusLoading,
  checkoutLoading,
  startCheckout,
  billingPortalLoading,
  openBillingPortal,
  onCancelled,
}) => {
  const { t, i18n } = useTranslation();
  const { showError } = useToast();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const requiresBillingAttention = billingStatus?.requiresPaymentMethod === true
    || (isTrialPlan(billingStatus?.currentPlan) && billingStatus?.hasPaymentDetails === false);

  const showSubscribeAction = requiresBillingAttention && isTrialPlan(billingStatus?.currentPlan);

  const trialEndDateLabel = useMemo(
    () => formatTrialEndDate(billingStatus?.trialExpiresAtUtc),
    [billingStatus?.trialExpiresAtUtc]
  );

  const nextBillingDateLabel = useMemo(
    () => formatTrialEndDate(billingStatus?.nextBillingAtUtc),
    [billingStatus?.nextBillingAtUtc]
  );

  const nextBillingPriceLabel = useMemo(
    () => formatSubscriptionPrice(
      billingStatus?.discountedPostTrialMonthlyAmount ?? billingStatus?.postTrialMonthlyAmount,
      billingStatus?.postTrialCurrency,
      i18n.language
    ),
    [
      billingStatus?.discountedPostTrialMonthlyAmount,
      billingStatus?.postTrialMonthlyAmount,
      billingStatus?.postTrialCurrency,
      i18n.language,
    ]
  );

  const originalNextBillingPriceLabel = useMemo(
    () => (billingStatus?.activePlanDiscountPercent
      ? formatSubscriptionPrice(
        billingStatus?.postTrialMonthlyAmount,
        billingStatus?.postTrialCurrency,
        i18n.language
      )
      : null),
    [billingStatus?.activePlanDiscountPercent, billingStatus?.postTrialMonthlyAmount, billingStatus?.postTrialCurrency, i18n.language]
  );

  const postTrialPriceLabel = useMemo(
    () => formatSubscriptionPrice(
      billingStatus?.discountedPostTrialMonthlyAmount ?? billingStatus?.postTrialMonthlyAmount,
      billingStatus?.postTrialCurrency,
      i18n.language
    ),
    [
      billingStatus?.discountedPostTrialMonthlyAmount,
      billingStatus?.postTrialMonthlyAmount,
      billingStatus?.postTrialCurrency,
      i18n.language,
    ]
  );

  const originalPostTrialPriceLabel = useMemo(
    () => (billingStatus?.activePlanDiscountPercent
      ? formatSubscriptionPrice(
        billingStatus?.postTrialMonthlyAmount,
        billingStatus?.postTrialCurrency,
        i18n.language
      )
      : null),
    [billingStatus?.activePlanDiscountPercent, billingStatus?.postTrialMonthlyAmount, billingStatus?.postTrialCurrency, i18n.language]
  );

  const isOnTrial = isTrialPlan(billingStatus?.currentPlan) && Boolean(billingStatus?.trialExpiresAtUtc);

  const entitlements = billingStatus?.entitlements;
  const isProPlan = isEffectivePro(entitlements) || billingStatus?.isEffectivePro === true;
  const isLapsedPlan = Boolean(billingStatus) && Boolean(entitlements?.isLapsedPro || billingStatus?.isLapsedPro);
  const isFreePlan = Boolean(billingStatus) && !isProPlan && !isLapsedPlan;

  const isActiveBasePlan = !isOnTrial
    && isBaseSubscriptionPlan(billingStatus?.currentPlan)
    && Boolean(billingStatus?.nextBillingAtUtc);

  const cancelEffectiveDateLabel = useMemo(
    () => formatTrialEndDate(billingStatus?.cancelsAtUtc ?? billingStatus?.nextBillingAtUtc),
    [billingStatus?.cancelsAtUtc, billingStatus?.nextBillingAtUtc]
  );

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      await StoreService.cancelSubscription(storeId);
      onCancelled?.();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      showError(err?.response?.data?.message || t('paymentSettingsPage.subscription.cancelError'));
    } finally {
      setCancelLoading(false);
      setCancelDialogOpen(false);
    }
  };

  return (
    <>
      <Box sx={{ p: sectionContentPadding }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SubscriptionsIcon sx={{ color: BRAND.accentText }} />
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600 }}>
            {t('paymentSettingsPage.subscription.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('paymentSettingsPage.subscription.description')}
        </Typography>

        <Box sx={{
          ...innerSectionStyles,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {billingStatusLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: BRAND.accent }} />
              <Typography variant="body2" color="text.secondary">
                {t('paymentSettingsPage.subscription.loading')}
              </Typography>
            </Box>
          ) : isLapsedPlan ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <WarningAmberIcon sx={{ color: '#ef6c00', fontSize: 22, flexShrink: 0 }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {t('storePlan.lapsedTitle')}
                </Typography>
                <Chip
                  size="small"
                  label={t('storePlan.lapsedBadge')}
                  sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('storePlan.lapsedDescription')}
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
              }}>
                <PrimaryActionButton
                  onClick={startCheckout}
                  disabled={checkoutLoading || billingStatusLoading}
                  startIcon={checkoutLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon />}
                >
                  {checkoutLoading ? t('stores.checkout.opening') : t('storePlan.reactivateAction')}
                </PrimaryActionButton>
                <PrimaryActionButton
                  variant="outlined"
                  onClick={openBillingPortal}
                  disabled={billingPortalLoading || billingStatusLoading}
                  startIcon={billingPortalLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon />}
                  sx={{ borderColor: BRAND.accent, color: BRAND.accentText }}
                >
                  {billingPortalLoading ? t('stores.billing.opening') : t('stores.billing.action')}
                </PrimaryActionButton>
              </Box>
            </>
          ) : isFreePlan ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 22, flexShrink: 0 }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {t('storePlan.freePlanTitle')}
                </Typography>
                <Chip
                  size="small"
                  label={t('storePlan.freeBadge')}
                  sx={{ backgroundColor: BRAND.background, color: BRAND.accentText }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('storePlan.freePlanDescription')}
              </Typography>
              <PrimaryActionButton
                onClick={startCheckout}
                disabled={checkoutLoading || billingStatusLoading}
                startIcon={checkoutLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon />}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                {checkoutLoading ? t('stores.checkout.opening') : t('storePlan.upgradeToPro')}
              </PrimaryActionButton>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {requiresBillingAttention ? (
                    <WarningAmberIcon sx={{ color: '#ef6c00', fontSize: 22, flexShrink: 0 }} />
                  ) : (
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 22, flexShrink: 0 }} />
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {requiresBillingAttention
                      ? t('paymentSettingsPage.subscription.statusActionRequired')
                      : t('paymentSettingsPage.subscription.statusActive')}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={requiresBillingAttention
                    ? t('paymentSettingsPage.subscription.billingDetailsPending')
                    : t('paymentSettingsPage.subscription.billingDetailsOnFile')}
                  sx={requiresBillingAttention
                    ? { backgroundColor: '#fff3e0', color: '#ef6c00' }
                    : { backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                {requiresBillingAttention
                  ? (billingStatus?.requiresPaymentMethod
                    ? t('stores.billing.issueDescription')
                    : t('stores.billing.description'))
                  : t('paymentSettingsPage.subscription.statusActiveDescription')}
              </Typography>

              {isOnTrial ? (
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      size="small"
                      label={t('paymentSettingsPage.subscription.trialUntil', { date: trialEndDateLabel })}
                      sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
                    />
                  </Box>
                  {postTrialPriceLabel ? (
                    <Box>
                      {billingStatus?.activePlanDiscountPercent ? (
                        <Chip
                          size="small"
                          label={t('paymentSettingsPage.subscription.discountOnNextPayment', {
                            percent: billingStatus.activePlanDiscountPercent,
                          })}
                          sx={{ mb: 1, backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                        />
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        {t('paymentSettingsPage.subscription.priceAfterTrial', {
                          price: postTrialPriceLabel,
                          date: trialEndDateLabel,
                        })}
                      </Typography>
                      {originalPostTrialPriceLabel ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                          {originalPostTrialPriceLabel}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                </Stack>
              ) : isActiveBasePlan ? (
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      size="small"
                      label={t('paymentSettingsPage.subscription.nextBillingOn', { date: nextBillingDateLabel })}
                      sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
                    />
                  </Box>
                  {nextBillingPriceLabel ? (
                    <Box>
                      {billingStatus?.activePlanDiscountPercent ? (
                        <Chip
                          size="small"
                          label={t('paymentSettingsPage.subscription.discountOnNextPayment', {
                            percent: billingStatus.activePlanDiscountPercent,
                          })}
                          sx={{ mb: 1, backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                        />
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        {t('paymentSettingsPage.subscription.priceOnNextBilling', {
                          price: nextBillingPriceLabel,
                          date: nextBillingDateLabel,
                        })}
                      </Typography>
                      {originalNextBillingPriceLabel ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                          {originalNextBillingPriceLabel}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                </Stack>
              ) : billingStatus?.currentPlan ? (
                <Chip
                  size="small"
                  label={String(billingStatus.currentPlan)}
                  sx={{ backgroundColor: '#fff3e0', color: '#ef6c00', alignSelf: 'flex-start' }}
                />
              ) : null}

              {billingStatus?.activeTeamMemberCount != null ? (
                <Stack spacing={0.5}>
                  {billingStatus.additionalStoreCount > 0 && billingStatus.additionalStoreUnitAmount != null ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('paymentSettingsPage.subscription.additionalStores', {
                        count: billingStatus.additionalStoreCount,
                        price: formatSubscriptionPrice(
                          billingStatus.additionalStoreUnitAmount,
                          billingStatus.postTrialCurrency,
                          i18n.language
                        ),
                      })}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    {t('paymentSettingsPage.subscription.teamMemberSeats', {
                      active: billingStatus.activeTeamMemberCount,
                      included: billingStatus.includedTeamMembers ?? 1,
                    })}
                  </Typography>
                  {(
                    (billingStatus.seatBreakdown?.length > 1
                      ? billingStatus.aggregatedAdditionalSeats
                      : billingStatus.projectedAdditionalSeats) ?? 0
                  ) > 0 && billingStatus.additionalSeatUnitAmount != null ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('paymentSettingsPage.subscription.additionalTeamMemberSeats', {
                        count: billingStatus.seatBreakdown?.length > 1
                          ? billingStatus.aggregatedAdditionalSeats
                          : billingStatus.projectedAdditionalSeats,
                        price: formatSubscriptionPrice(
                          billingStatus.additionalSeatUnitAmount,
                          billingStatus.postTrialCurrency,
                          i18n.language
                        ),
                      })}
                    </Typography>
                  ) : null}
                  {(billingStatus.aggregatedAdditionalSeats ?? 0) > 0 && billingStatus.seatBreakdown?.length > 1 ? (
                    <Stack spacing={0.25} sx={{ pl: 1 }}>
                      {billingStatus.seatBreakdown
                        .filter((row) => row.additionalSeatCount > 0)
                        .map((row) => (
                          <Typography key={row.storeId} variant="caption" color="text.secondary">
                            {t('paymentSettingsPage.subscription.seatBreakdownRow', {
                              store: row.storeName || t('paymentSettingsPage.subscription.unnamedStore'),
                              count: row.additionalSeatCount,
                            })}
                          </Typography>
                        ))}
                    </Stack>
                  ) : null}
                  {billingStatus.estimatedMonthlyTotal != null ? (
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1.5,
                        borderTop: `1px solid ${brandAlpha('accent', 0.25)}`,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: BRAND.primaryText,
                          letterSpacing: '0.01em',
                        }}
                      >
                        {t('paymentSettingsPage.subscription.estimatedMonthlyTotal', {
                          price: formatSubscriptionPrice(
                            billingStatus.estimatedMonthlyTotal,
                            billingStatus.postTrialCurrency,
                            i18n.language
                          ),
                        })}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              ) : null}

              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                width: { xs: '100%', sm: 'auto' },
              }}>
                {showSubscribeAction ? (
                  <PrimaryActionButton
                    onClick={startCheckout}
                    disabled={checkoutLoading || billingStatusLoading}
                    startIcon={checkoutLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon />}
                  >
                    {checkoutLoading ? t('stores.checkout.opening') : t('stores.checkout.action')}
                  </PrimaryActionButton>
                ) : null}
                <PrimaryActionButton
                  variant={showSubscribeAction ? 'outlined' : 'contained'}
                  onClick={openBillingPortal}
                  disabled={billingPortalLoading || billingStatusLoading}
                  startIcon={billingPortalLoading ? <CircularProgress size={16} color="inherit" /> : <CreditCardIcon />}
                  sx={showSubscribeAction ? { borderColor: BRAND.accent, color: BRAND.accentText } : undefined}
                >
                  {billingPortalLoading ? t('stores.billing.opening') : t('stores.billing.action')}
                </PrimaryActionButton>
                {isActiveBasePlan && !billingStatus?.cancelAtPeriodEnd ? (
                  <PrimaryActionButton
                    onClick={() => setCancelDialogOpen(true)}
                    disabled={cancelLoading || billingStatusLoading}
                    sx={{
                      background: '#fdecea',
                      color: '#c62828',
                      boxShadow: 'none',
                      border: '1px solid #f5c2c0',
                      '&:hover': {
                        background: '#f9d4d2',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {t('paymentSettingsPage.subscription.cancelAction')}
                  </PrimaryActionButton>
                ) : null}
              </Box>

              {billingStatus?.cancelAtPeriodEnd ? (
                <Alert severity="info">
                  {t('paymentSettingsPage.subscription.cancelScheduled', { date: cancelEffectiveDateLabel })}
                </Alert>
              ) : null}

              {isOnTrial ? (
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: BRAND.background,
                  border: `1px solid ${BRAND.line}`,
                  width: '100%',
                }}>
                  <Typography
                    variant="caption"
                    sx={{ color: BRAND.textMuted, lineHeight: 1.55, display: 'block' }}
                  >
                    {t('paymentSettingsPage.subscription.cancelInstructions')}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelSubscription}
        title={t('paymentSettingsPage.subscription.cancelTitle')}
        message={t('paymentSettingsPage.subscription.cancelMessage', { date: cancelEffectiveDateLabel })}
        confirmText={t('paymentSettingsPage.subscription.cancelConfirm')}
        loading={cancelLoading}
      />
    </>
  );
};

export default StoreSubscriptionPanel;
